import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

const ResourceList = () => {
  const { formatMessage: t } = useIntl();
  const {
    resourceService,
    resourceDetailsUrl,
    resourceCreateUrl,
  } = useGlobalContext();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("updatedOn");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.resourceFilterSearchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm?.trim(), 500);
  const { organizationId } = contextStore.useState();

  useListTitle("facultyLabel");

  const { isError, data, error, isFetching } = useQuery(
    [
      "resources",
      page,
      rowsPerPage,
      order,
      orderBy,
      debouncedSearchTerm,
      organizationId,
    ],
    () =>
      resourceService.getResources(
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: BaseFilter.fromJS({
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "prefix",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "prefixLabel" }),
    },
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "crmEmail",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "emailLabel" }),
    },
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
    {
      accessor: "summary",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "descriptionLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(resourceDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link to={resourceCreateUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary">
            <AddIcon />
          </Fab>
        </Link>
      </Box>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={12}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.resourceFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.resourceFilterSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: data?.data ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "facultyLabel" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => resourceDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default ResourceList;
